import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { Phone, MessageSquare, Send, UserCog, CheckCircle2, RefreshCcw } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const KIND_LABEL = {
    birthday: "Birthday",
    collection_loan: "Loan Receipt",
    collection_pigmy: "Saving Scheme Receipt",
    welcome_loan: "Loan Welcome",
    welcome_pigmy: "Saving Scheme Welcome",
    month_end: "Month-end Summary",
};
const KIND_STYLE = {
    birthday: "bg-pink-50 text-pink-800 border-pink-200",
    collection_loan: "bg-blue-50 text-blue-800 border-blue-200",
    collection_pigmy: "bg-emerald-50 text-emerald-800 border-emerald-200",
    welcome_loan: "bg-indigo-50 text-indigo-800 border-indigo-200",
    welcome_pigmy: "bg-teal-50 text-teal-800 border-teal-200",
    month_end: "bg-amber-50 text-amber-800 border-amber-200",
};
const STATUS_STYLE = {
    new: "bg-slate-100 text-slate-700 border-slate-200",
    assigned: "bg-blue-50 text-blue-900 border-blue-200",
    done: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const normalizeMobile = (m) => (m || "").replace(/[^\d+]/g, "");
const waNumber = (m) => {
    let s = normalizeMobile(m);
    if (s.startsWith("+")) s = s.slice(1);
    // Assume India (91) if 10-digit local.
    if (s.length === 10) s = "91" + s;
    return s;
};

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

// Admin + Super-Admin see everything scoped by branch; Employees see only tasks assigned to them.
// Backend enforces this; the UI just displays.
// Set `embed` = true to render inside another page (skips outer container + heading).
export default function ReminderTasks({ embed = false }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";

    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [kindFilter, setKindFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [assignTarget, setAssignTarget] = useState(null);
    const [assignUsername, setAssignUsername] = useState("");

    async function load() {
        setBusy(true);
        try {
            const params = {};
            if (kindFilter !== "all") params.kind = kindFilter;
            if (statusFilter !== "all") params.status = statusFilter;
            const { data } = await api.get("/reminder-tasks", { params });
            setItems(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }
    useEffect(() => { load(); }, [kindFilter, statusFilter]);

    useEffect(() => {
        if (!isAdmin) return;
        (async () => {
            try {
                const { data } = await api.get("/users");
                setEmployees((data || []).filter((u) => u.role === "employee" && !u.disabled));
            } catch { /* ignore */ }
        })();
    }, [isAdmin]);

    async function markDone(id) {
        try {
            await api.post(`/reminder-tasks/${id}/done`);
            setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: "done" } : x)));
            toast.success("Task marked as done");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function submitAssign() {
        if (!assignTarget) return;
        try {
            await api.post(`/reminder-tasks/${assignTarget.id}/assign`, {
                assigned_to: assignUsername || null,
            });
            const emp = employees.find((e) => e.username === assignUsername);
            setItems((prev) => prev.map((x) =>
                x.id === assignTarget.id
                    ? { ...x, assigned_to: assignUsername || null, assigned_to_name: emp?.name || null, status: assignUsername ? "assigned" : "new" }
                    : x,
            ));
            toast.success(assignUsername ? `Assigned to ${emp?.name || assignUsername}` : "Assignment cleared");
            setAssignTarget(null);
            setAssignUsername("");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const term = q.trim().toLowerCase();
    const visible = useMemo(() => (
        term ? items.filter((x) =>
                  [x.customer_name, x.customer_code, x.customer_mobile, x.title, x.message]
                      .filter(Boolean)
                      .some((f) => f.toLowerCase().includes(term)),
              ) : items
    ), [items, term]);

    const kinds = Object.keys(KIND_LABEL);

    return (
        <div className={embed ? "space-y-4" : "max-w-6xl mx-auto p-4 sm:p-6 space-y-4"}>
            {!embed && (
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                            Customer Reminders
                        </h1>
                        <p className="text-sm text-slate-500">
                            {isAdmin
                                ? "Birthdays, collection receipts, welcome and month-end tasks. Assign to an employee or send yourself."
                                : "Tasks assigned to you. Open the customer's dialer, SMS or WhatsApp and mark done when handled."}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={load}
                            disabled={busy}
                            data-testid="rt-refresh"
                            className="h-10 rounded-xl"
                        >
                            <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
                        </Button>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
                <Select value={kindFilter} onValueChange={setKindFilter}>
                    <SelectTrigger className="h-10 rounded-xl w-52" data-testid="rt-filter-kind">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All kinds</SelectItem>
                        {kinds.map((k) => (
                            <SelectItem key={k} value={k}>{KIND_LABEL[k]}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-10 rounded-xl w-40" data-testid="rt-filter-status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="assigned">Assigned</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                    </SelectContent>
                </Select>
                <Input
                    placeholder="Search name / code / mobile / message…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="h-10 rounded-xl max-w-sm"
                    data-testid="rt-search"
                />
            </div>

            <Card className="rounded-2xl border-slate-100 p-0 overflow-hidden">
                {visible.length === 0 ? (
                    <div className="py-14 text-center text-sm text-slate-400">
                        {busy ? "Loading…" : "No reminders here yet."}
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {visible.map((r) => (
                            <ReminderRow
                                key={r.id}
                                r={r}
                                isAdmin={isAdmin}
                                onOpenAssign={() => { setAssignTarget(r); setAssignUsername(r.assigned_to || ""); }}
                                onDone={() => markDone(r.id)}
                            />
                        ))}
                    </div>
                )}
            </Card>

            <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Assign to employee</DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-slate-500">
                        {assignTarget?.customer_name} · {assignTarget?.customer_code}
                    </p>
                    <Select value={assignUsername || "__unassigned"} onValueChange={(v) => setAssignUsername(v === "__unassigned" ? "" : v)}>
                        <SelectTrigger className="h-11 rounded-xl" data-testid="rt-assign-select">
                            <SelectValue placeholder="Choose an employee" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__unassigned">— Unassigned —</SelectItem>
                            {employees.map((e) => (
                                <SelectItem key={e.username} value={e.username}>
                                    {e.name} <span className="text-slate-500">@{e.username}</span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <DialogFooter>
                        <Button
                            onClick={submitAssign}
                            data-testid="rt-assign-save"
                            className="bg-blue-900 hover:bg-blue-800"
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ReminderRow({ r, isAdmin, onOpenAssign, onDone }) {
    const tel = normalizeMobile(r.customer_mobile);
    const smsHref = tel ? `sms:${tel}?body=${encodeURIComponent(r.message || "")}` : "#";
    const waHref = tel ? `https://wa.me/${waNumber(tel)}?text=${encodeURIComponent(r.message || "")}` : "#";
    const disabled = !tel;
    return (
        <div className="p-4 sm:p-5 flex flex-col gap-3" data-testid={`rt-row-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={KIND_STYLE[r.kind] || "bg-slate-100 text-slate-700"}>
                            {KIND_LABEL[r.kind] || r.kind}
                        </Badge>
                        <Badge className={STATUS_STYLE[r.status] || "bg-slate-100 text-slate-700"}>
                            {r.status}
                        </Badge>
                        <span className="text-xs text-slate-500">{fmt(r.created_at)}</span>
                        {r.assigned_to_name && (
                            <span className="text-xs text-slate-500">
                                → <span className="font-semibold text-slate-700">{r.assigned_to_name}</span>
                            </span>
                        )}
                    </div>
                    <div className="font-semibold text-slate-900">{r.title}</div>
                    <div className="text-xs text-slate-500">
                        {r.customer_name} · {r.customer_code} · {r.customer_mobile || "—"}
                    </div>
                    <pre className="text-xs sm:text-[13px] text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-xl p-3 leading-relaxed font-sans">
                        {r.message}
                    </pre>
                </div>
            </div>
            <div className="flex flex-wrap gap-2">
                <a
                    href={tel ? `tel:${tel}` : undefined}
                    onClick={(e) => disabled && e.preventDefault()}
                    className={`inline-flex items-center gap-1 h-9 px-3 rounded-lg border text-sm font-semibold ${
                        disabled ? "bg-slate-50 text-slate-400 border-slate-200" : "bg-white text-slate-800 border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`rt-call-${r.id}`}
                >
                    <Phone className="h-4 w-4" /> Call
                </a>
                <a
                    href={disabled ? undefined : smsHref}
                    onClick={(e) => disabled && e.preventDefault()}
                    className={`inline-flex items-center gap-1 h-9 px-3 rounded-lg border text-sm font-semibold ${
                        disabled ? "bg-slate-50 text-slate-400 border-slate-200" : "bg-white text-slate-800 border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`rt-sms-${r.id}`}
                >
                    <MessageSquare className="h-4 w-4" /> SMS
                </a>
                <a
                    href={disabled ? undefined : waHref}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => disabled && e.preventDefault()}
                    className={`inline-flex items-center gap-1 h-9 px-3 rounded-lg border text-sm font-semibold ${
                        disabled ? "bg-slate-50 text-slate-400 border-slate-200" : "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700"
                    }`}
                    data-testid={`rt-wa-${r.id}`}
                >
                    <Send className="h-4 w-4" /> WhatsApp
                </a>
                {isAdmin && (
                    <Button
                        variant="outline"
                        onClick={onOpenAssign}
                        className="h-9 rounded-lg"
                        data-testid={`rt-assign-${r.id}`}
                    >
                        <UserCog className="h-4 w-4 mr-1" />
                        Assign
                    </Button>
                )}
                {r.status !== "done" && (
                    <Button
                        onClick={onDone}
                        className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`rt-done-${r.id}`}
                    >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Mark done
                    </Button>
                )}
            </div>
        </div>
    );
}
